import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatIcon } from '@angular/material/icon'
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { format, parseISO } from 'date-fns'
import { map, Observable, of } from 'rxjs'
import { AvatarComponent } from '../../avatar/avatar.component'
import { AdminAgendaAttendee, Call } from '../../calls.service'
import { ProgressLoaderComponent } from '../../common/progress-loader/progress-loader.component'
import {
  ActionItemGroup,
  ActionItemGroupKey,
  ActionItemsView,
  dueLabel,
  DueLabel,
  groupByType,
  inMeetingOrder,
  isAwaitingActionItemDetection,
  toIsoDate,
} from './action-items-view'
import { ActionItem, ActionItemAssigneeInput, ActionItemPassage, ActionItemsOverview, ActionItemsService, toSaveActionItem } from './action-items.service'

type DueMode = 'AS_SAID' | 'DATE'

/** A new action item that isn't saved until it's approved. */
type Draft = {
  description: string
  assignees: ActionItemAssigneeInput[]
  dueDate: string | null
}

/** Which assignee menu is open: a saved item's, or the draft's. */
type AssigneeMenuKey = number | 'DRAFT'

@Component({
  selector: 'app-action-items',
  standalone: true,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIcon,
    MatMenuModule,
    MatTooltip,
    TranslatePipe,
    TranslateDirective,
    AvatarComponent,
    ProgressLoaderComponent,
  ],
  templateUrl: './action-items.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './action-items.component.scss',
})
export class ActionItemsComponent implements OnChanges {
  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  canUserEdit!: boolean

  private actionItemsService = inject(ActionItemsService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private snackBar = inject(MatSnackBar)
  private translateService = inject(TranslateService)

  private createMutation = this.actionItemsService.createMutation()
  private updateMutation = this.actionItemsService.updateMutation()
  private markMutation = this.actionItemsService.markAsNotAnActionMutation()
  private addDetailMutation = this.actionItemsService.addDetailMutation()

  overview$: Observable<ActionItemsOverview | null> = of(null)

  view: ActionItemsView = 'BY_TYPE'
  collapsedGroups = new Set<ActionItemGroupKey>(['NOT_AN_ACTION'])
  activePassageKey: string | null = null

  editingId: number | null = null
  editValue = ''
  draft: Draft | null = null

  detailLoadingId: number | null = null
  /** Items whose add-detail was cancelled while the request was still running; the original description is saved back when it returns. */
  cancelledDetailIds = new Set<number>()
  /** The description each item had before detail was added in this session, for Undo. */
  descriptionBeforeDetail = new Map<number, string>()

  assigneeSelection: ActionItemAssigneeInput[] = []
  /** The assignee menu that is open or saving, so its cell shows the selection rather than the saved assignees. */
  assigneeMenuKey: AssigneeMenuKey | null = null
  newAssigneeName: string | null = null

  dueMode: DueMode = 'AS_SAID'
  dueSelection: Date | null = null

  @ViewChild('draftInput')
  set draftInput(input: ElementRef<HTMLInputElement> | undefined) {
    input?.nativeElement.focus()
  }

  @ViewChild('editInput')
  set editInput(input: ElementRef<HTMLTextAreaElement> | undefined) {
    if (input !== undefined) {
      const element = input.nativeElement
      element.focus()
      element.setSelectionRange(element.value.length, element.value.length)
    }
  }

  @ViewChild('newNameInput')
  set newNameInput(input: ElementRef<HTMLInputElement> | undefined) {
    input?.nativeElement.focus()
  }

  readonly skeletonRows: { lines: string[]; assigneeWidth: number; dueWidth: number }[] = [
    { lines: ['86%', '48%'], assigneeWidth: 64, dueWidth: 70 },
    { lines: ['72%'], assigneeWidth: 64, dueWidth: 48 },
    { lines: ['90%', '36%'], assigneeWidth: 40, dueWidth: 70 },
    { lines: ['64%'], assigneeWidth: 64, dueWidth: 48 },
    { lines: ['78%', '54%'], assigneeWidth: 64, dueWidth: 70 },
    { lines: ['58%'], assigneeWidth: 40, dueWidth: 48 },
  ]

  readonly views: { view: ActionItemsView; labelKey: string }[] = [
    { view: 'BY_TYPE', labelKey: 'ACTION_ITEMS.VIEWS.BY_TYPE' },
    { view: 'IN_MEETING_ORDER', labelKey: 'ACTION_ITEMS.VIEWS.IN_MEETING_ORDER' },
  ]

  ngOnChanges(changes: SimpleChanges) {
    const change = changes['call']
    if (change !== undefined && (change.firstChange || (change.previousValue as Call).id !== this.call.id)) {
      this.overview$ = this.actionItemsService.list(this.call.id).pipe(map((result) => result.data ?? null))
    }
  }

  isAwaitingDetection(overview: ActionItemsOverview): boolean {
    return isAwaitingActionItemDetection(overview, this.call)
  }

  get attendees(): AdminAgendaAttendee[] {
    return this.call.agendaMetadata.attendees
  }

  groups(items: ActionItem[]): ActionItemGroup[] {
    return groupByType(items)
  }

  ordered(items: ActionItem[]): ActionItem[] {
    return inMeetingOrder(items)
  }

  isCollapsed(key: ActionItemGroupKey): boolean {
    return this.collapsedGroups.has(key)
  }

  toggleGroup(key: ActionItemGroupKey) {
    if (this.collapsedGroups.has(key)) {
      this.collapsedGroups.delete(key)
    } else {
      this.collapsedGroups.add(key)
    }
  }

  due(item: Pick<ActionItem, 'dueDate' | 'dueText'>): DueLabel {
    return dueLabel(item)
  }

  editedNoteKey(item: ActionItem): string | null {
    if (item.isMarkedAsNotAnAction) {
      return null
    }
    if (item.isUserCreated) {
      return 'ACTION_ITEMS.ADDED_MANUALLY'
    }
    return item.editedAt === null ? null : 'ACTION_ITEMS.EDITED'
  }

  canAddDetail(item: ActionItem): boolean {
    return (
      this.canUserEdit &&
      !item.isMarkedAsNotAnAction &&
      this.detailLoadingId === null &&
      !this.cancelledDetailIds.has(item.id) &&
      !this.descriptionBeforeDetail.has(item.id) &&
      item.passages.length > 0
    )
  }

  passageKey(item: ActionItem, passage: ActionItemPassage): string {
    return `${item.id}-${passage.startSentenceId}`
  }

  showPassage(item: ActionItem, passage: ActionItemPassage) {
    this.activePassageKey = this.passageKey(item, passage)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sentenceId: passage.startSentenceId, endSentenceId: passage.endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
    })
  }

  startEdit(item: ActionItem) {
    if (!this.canUserEdit || item.isMarkedAsNotAnAction || this.detailLoadingId === item.id) {
      return
    }
    this.editingId = item.id
    this.editValue = item.description
  }

  cancelEdit() {
    this.editingId = null
  }

  async saveEdit(item: ActionItem) {
    if (this.editingId !== item.id) {
      return
    }
    this.editingId = null
    const description = this.editValue.trim()
    if (description === '' || description === item.description) {
      return
    }
    await this.save(item, { description })
  }

  onEditKeydown(event: KeyboardEvent, item: ActionItem) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void this.saveEdit(item)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancelEdit()
    }
  }

  async addDetail(item: ActionItem) {
    this.detailLoadingId = item.id
    this.editingId = null
    try {
      await this.addDetailMutation.mutateAsync({ callId: this.call.id, actionItemId: item.id })
      if (this.cancelledDetailIds.has(item.id)) {
        await this.save(item, { description: item.description })
      } else {
        this.descriptionBeforeDetail.set(item.id, item.description)
      }
    } catch {
      // The mutation already shows an error toast.
    } finally {
      this.cancelledDetailIds.delete(item.id)
      if (this.detailLoadingId === item.id) {
        this.detailLoadingId = null
      }
    }
  }

  cancelDetail() {
    if (this.detailLoadingId !== null) {
      this.cancelledDetailIds.add(this.detailLoadingId)
      this.detailLoadingId = null
    }
  }

  async undoDetail(item: ActionItem) {
    const previous = this.descriptionBeforeDetail.get(item.id) ?? null
    if (previous === null) {
      return
    }
    this.descriptionBeforeDetail.delete(item.id)
    await this.save(item, { description: previous })
  }

  async markAsNotAnAction(item: ActionItem) {
    const marked = await this.setMarked(item, true)
    if (!marked) {
      return
    }
    this.snackBar
      .open(this.translateService.instant('ACTION_ITEMS.MARKED_AS_NOT_AN_ACTION'), this.translateService.instant('ACTION_ITEMS.UNDO'), { duration: 10000 })
      .onAction()
      .subscribe(() => void this.setMarked(item, false))
  }

  async restore(item: ActionItem) {
    await this.setMarked(item, false)
  }

  addAction() {
    this.draft = { description: '', assignees: [], dueDate: null }
    this.collapsedGroups.delete('AGREED')
  }

  cancelDraft() {
    this.draft = null
  }

  async saveDraft() {
    const draft = this.draft
    if (draft === null || draft.description.trim() === '') {
      return
    }
    try {
      await this.createMutation.mutateAsync({
        callId: this.call.id,
        data: { description: draft.description.trim(), type: 'AGREED', assignees: draft.assignees, dueText: null, dueDate: draft.dueDate },
      })
      this.draft = null
    } catch {
      // The mutation already shows an error toast; the draft stays so nothing typed is lost.
    }
  }

  onDraftKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      void this.saveDraft()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.cancelDraft()
    }
  }

  openAssignees(key: AssigneeMenuKey, assignees: ActionItemAssigneeInput[]) {
    this.assigneeSelection = assignees.map(({ name, callAgendaAttendeeId }) => ({ name, callAgendaAttendeeId }))
    this.assigneeMenuKey = key
    this.newAssigneeName = null
  }

  shownAssignees(key: AssigneeMenuKey, assignees: ActionItemAssigneeInput[]): ActionItemAssigneeInput[] {
    return this.assigneeMenuKey === key ? this.assigneeSelection : assignees
  }

  /** Names picked that aren't agenda attendees, so they stay listed while the menu is open. */
  otherAssignees(assignees: ActionItemAssigneeInput[]): ActionItemAssigneeInput[] {
    const names = [...assignees, ...this.assigneeSelection].filter((assignee) => assignee.callAgendaAttendeeId === null).map((assignee) => assignee.name)
    return [...new Set(names)].map((name) => ({ name, callAgendaAttendeeId: null }))
  }

  attendeeName(attendee: AdminAgendaAttendee): string {
    return `${attendee.firstName} ${attendee.lastName}`.trim()
  }

  isAttendeeSelected(attendee: AdminAgendaAttendee): boolean {
    return this.assigneeSelection.some((assignee) => assignee.callAgendaAttendeeId === attendee.id)
  }

  isNameSelected(name: string): boolean {
    return this.assigneeSelection.some((assignee) => assignee.callAgendaAttendeeId === null && assignee.name === name)
  }

  toggleAttendee(attendee: AdminAgendaAttendee) {
    this.assigneeSelection = this.isAttendeeSelected(attendee)
      ? this.assigneeSelection.filter((assignee) => assignee.callAgendaAttendeeId !== attendee.id)
      : [...this.assigneeSelection, { name: this.attendeeName(attendee), callAgendaAttendeeId: attendee.id }]
  }

  toggleName(name: string) {
    this.assigneeSelection = this.isNameSelected(name)
      ? this.assigneeSelection.filter((assignee) => assignee.callAgendaAttendeeId !== null || assignee.name !== name)
      : [...this.assigneeSelection, { name, callAgendaAttendeeId: null }]
  }

  startNewAssignee() {
    this.newAssigneeName = ''
  }

  addNewAssignee() {
    const name = (this.newAssigneeName ?? '').trim()
    this.newAssigneeName = null
    if (name !== '' && !this.isNameSelected(name)) {
      this.assigneeSelection = [...this.assigneeSelection, { name, callAgendaAttendeeId: null }]
    }
  }

  async saveAssignees(item: ActionItem) {
    const selection = this.assigneeSelection
    const before = JSON.stringify(item.assignees.map(({ name, callAgendaAttendeeId }) => ({ name, callAgendaAttendeeId })))
    try {
      if (JSON.stringify(selection) !== before) {
        await this.save(item, { assignees: selection })
      }
    } finally {
      if (this.assigneeMenuKey === item.id) {
        this.assigneeMenuKey = null
      }
    }
  }

  closeDraftAssignees() {
    if (this.draft !== null) {
      this.draft = { ...this.draft, assignees: this.assigneeSelection }
    }
    if (this.assigneeMenuKey === 'DRAFT') {
      this.assigneeMenuKey = null
    }
  }

  openDue(due: Pick<ActionItem, 'dueDate' | 'dueText'>) {
    this.dueMode = due.dueDate !== null || due.dueText === null ? 'DATE' : 'AS_SAID'
    this.dueSelection = due.dueDate === null ? null : parseISO(due.dueDate)
  }

  selectDueDate(date: Date | null) {
    this.dueSelection = date
    this.dueMode = 'DATE'
  }

  /** Applies the due date menu to a saved item, or to the draft when there is no item. */
  async applyDue(item: ActionItem | null, trigger: MatMenuTrigger) {
    trigger.closeMenu()
    const dueDate = this.dueMode === 'DATE' && this.dueSelection !== null ? toIsoDate(this.dueSelection) : null
    if (this.dueMode === 'DATE' && dueDate === null) {
      return
    }
    if (item === null) {
      if (this.draft !== null) {
        this.draft = { ...this.draft, dueDate }
      }
    } else if (dueDate !== item.dueDate) {
      await this.save(item, { dueDate })
    }
  }

  /** Keeps typing inside a menu from reaching the menu's own key handling, except Escape, which still closes it. */
  keepKeysInMenu(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      event.stopPropagation()
    }
  }

  formatDate(date: Date): string {
    return format(date, 'd MMM yyyy')
  }

  exportXlsx() {
    this.actionItemsService.export(this.call, 'xlsx').subscribe()
  }

  private async save(item: ActionItem, changes: Partial<ReturnType<typeof toSaveActionItem>>) {
    try {
      await this.updateMutation.mutateAsync({ callId: this.call.id, actionItemId: item.id, data: { ...toSaveActionItem(item), ...changes } })
    } catch {
      // The mutation already shows an error toast.
    }
  }

  private async setMarked(item: ActionItem, isMarkedAsNotAnAction: boolean): Promise<boolean> {
    try {
      await this.markMutation.mutateAsync({ callId: this.call.id, actionItemId: item.id, isMarkedAsNotAnAction })
      return true
    } catch {
      // The mutation already shows an error toast.
      return false
    }
  }
}
